import React from 'react'
import {
  Box,
  Typography,
  Chip,
  Stack,
  useTheme,
} from '@mui/material'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Cancel from '@mui/icons-material/Cancel'
import Psychology from '@mui/icons-material/Psychology'
import Schedule from '@mui/icons-material/Schedule'
import { styled } from '@mui/material/styles'

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '&:last-child': { marginBottom: 0 },
}))

const Label = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: theme.spacing(0.5),
}))

const Value = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  color: theme.palette.text.primary,
}))

/**
 * Read-only viewer for ScreenerProfile (from GET /api/secure/cc/support/screeners/profiles/by-mentor-id).
 * Displays practitionerId, userId, supportedTargetSpecialtySlugs, clinicIds, rostering, enabled, etc.
 */
export default function ScreenerProfileViewer({ profile }) {
  const theme = useTheme()

  if (!profile || typeof profile !== 'object') {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">No screener profile data available.</Typography>
      </Box>
    )
  }

  const slugs = profile.supportedTargetSpecialtySlugs ?? profile.supportedTargetSpecialtySlug ?? []
  const specialtySlugs = Array.isArray(slugs) ? slugs : (slugs ? [slugs] : [])
  const clinicIds = profile.clinicIds ?? []
  const clinicIdsArr = Array.isArray(clinicIds) ? clinicIds : (clinicIds ? [clinicIds] : [])
  const enabledProtocols = profile.enabledProtocols ?? []
  const protocolsArr = Array.isArray(enabledProtocols) ? enabledProtocols : (enabledProtocols ? [enabledProtocols] : [])

  return (
    <Box sx={{ px: 0, py: 1 }}>
      <Section>
        <Label>Practitioner &amp; User</Label>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {profile.practitionerId && (
            <Chip size="small" label={`Practitioner: ${profile.practitionerId}`} variant="outlined" />
          )}
          {profile.userId && (
            <Chip size="small" label={`User: ${profile.userId}`} variant="outlined" />
          )}
        </Stack>
      </Section>

      <Section>
        <Label>Status</Label>
        <Stack direction="row" spacing={1} alignItems="center">
          {profile.enabled != null && (
            profile.enabled ? (
              <Chip size="small" icon={<CheckCircle sx={{ fontSize: '16px !important' }} />} label="Enabled" color="success" />
            ) : (
              <Chip size="small" icon={<Cancel sx={{ fontSize: '16px !important' }} />} label="Disabled" color="default" />
            )
          )}
          {profile.rostering != null && (
            <Chip
              size="small"
              icon={<Schedule sx={{ fontSize: '16px !important' }} />}
              label={profile.rostering ? 'Rostering on' : 'Rostering off'}
              variant="outlined"
            />
          )}
        </Stack>
      </Section>

      {specialtySlugs.length > 0 && (
        <Section>
          <Label>Target specialities</Label>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {specialtySlugs.map((slug) => (
              <Chip key={slug} size="small" icon={<Psychology sx={{ fontSize: '14px !important' }} />} label={slug} />
            ))}
          </Stack>
        </Section>
      )}

      {(profile.supportedDomainSlugs?.length > 0 || (profile.supportedDomainSlug && [profile.supportedDomainSlug].length > 0)) && (
        <Section>
          <Label>Screening domains</Label>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {(Array.isArray(profile.supportedDomainSlugs) ? profile.supportedDomainSlugs : [profile.supportedDomainSlug]).map((d) => (
              <Chip key={d} size="small" label={d} variant="outlined" />
            ))}
          </Stack>
        </Section>
      )}

      {protocolsArr.length > 0 && (
        <Section>
          <Label>Enabled protocols</Label>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {protocolsArr.map((p) => (
              <Chip key={p} size="small" label={p} />
            ))}
          </Stack>
        </Section>
      )}

      {(clinicIdsArr.length > 0 || profile.attachedClinicBrandIds?.length > 0 || profile.attachedClinicLocationIds?.length > 0 || profile.attachedChamberRoomIds?.length > 0) && (
        <Section>
          <Label>Attachments</Label>
          <Stack spacing={0.5}>
            {(profile.attachedClinicBrandIds?.length > 0) && (
              <Value>Clinic brands: {profile.attachedClinicBrandIds.join(', ')}</Value>
            )}
            {(profile.attachedClinicLocationIds?.length > 0) && (
              <Value>Clinic locations: {profile.attachedClinicLocationIds.join(', ')}</Value>
            )}
            {(profile.attachedChamberRoomIds?.length > 0) && (
              <Value>Chamber rooms: {profile.attachedChamberRoomIds.join(', ')}</Value>
            )}
            {clinicIdsArr.length > 0 && profile.attachedClinicLocationIds?.length === 0 && (
              <Value>Clinic IDs: {clinicIdsArr.join(', ')}</Value>
            )}
          </Stack>
        </Section>
      )}

      {profile.screenerRoleIds?.length > 0 && (
        <Section>
          <Label>Roles</Label>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {profile.screenerRoleIds.map((r) => (
              <Chip key={r} size="small" label={r} />
            ))}
          </Stack>
        </Section>
      )}
    </Box>
  )
}
